import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { useLang } from "../../i18n/LangContext";
import { api, extractError } from "../../api/client";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { StatusBadge, EmptyState, formatIDR } from "../Shared";
import { CheckCircle2, Trash2 } from "lucide-react";

export function EnrollmentTable({ batchId }) {
  const { user } = useAuth();
  const { t } = useLang();
  const perms = user?.permissions || [];
  const canAssign = perms.includes("coach.assign");
  const canActivate = perms.includes("enrollment.manage");
  const isMasterAdmin = user?.role === "MASTER_ADMIN";
  const [rows, setRows] = useState([]);
  const [coaches, setCoaches] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/enrollments?batch_id=${batchId}`);
      setRows(data.data);
    } catch (e) { toast.error(extractError(e).message); }
  }, [batchId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (canAssign) api.get("/admin/coaches").then(({ data }) => setCoaches(data.data)).catch(() => {}); }, [canAssign]);

  const assign = async (id, coachId) => {
    try {
      await api.patch(`/admin/enrollments/${id}/coach`, { coach_user_id: coachId === "none" ? null : coachId });
      toast.success(t("programs.coachAssigned"));
      load();
    } catch (e) { toast.error(extractError(e).message); }
  };

  const activate = async (id) => {
    try {
      await api.post(`/admin/enrollments/${id}/activate`);
      toast.success(t("enrollments.activated"));
      load();
    } catch (e) { toast.error(extractError(e).message); }
  };

  const removeEnrollment = async (row) => {
    if (!window.confirm(t("del.confirmEnrollment").replace("{name}", row.member_name || row.participant_code))) return;
    try {
      await api.delete(`/admin/enrollments/${row.enrollment_id}`);
      toast.success(t("del.enrollmentDeleted"));
      load();
    } catch (e) { toast.error(extractError(e).message); }
  };

  if (rows.length === 0) return <EmptyState testId={`enrollments-empty-${batchId}`} text={t("enrollments.empty")} />;

  return (
    <div className="overflow-x-auto rounded-lg border border-white/5">
      <table data-testid={`enrollments-table-${batchId}`} className="w-full text-sm">
        <thead className="text-[11px] uppercase tracking-wider text-slate-500 bg-white/[0.02]">
          <tr>
            <th className="text-left px-3 py-2 font-medium">{t("payments.participantCode")}</th>
            <th className="text-left px-3 py-2 font-medium">{t("admin.members")}</th>
            <th className="text-right px-3 py-2 font-medium">{t("payments.totalToPay")}</th>
            <th className="text-left px-3 py-2 font-medium">{t("common.status")}</th>
            <th className="text-left px-3 py-2 font-medium">Coach</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {rows.map((e) => (
            <tr key={e.enrollment_id} data-testid={`enrollment-admin-row-${e.enrollment_id}`} className="border-t border-white/5">
              <td className="px-3 py-2 font-mono text-emerald-300 font-semibold">{e.participant_code}</td>
              <td className="px-3 py-2"><div className="text-white">{e.member_name}</div><div className="text-xs text-slate-500">{e.member_email}</div></td>
              <td className="px-3 py-2 text-right text-[#F59E0B] font-semibold">{formatIDR(e.total_amount)}</td>
              <td className="px-3 py-2"><StatusBadge status={e.status} testId={`enrollment-admin-status-${e.enrollment_id}`} /></td>
              <td className="px-3 py-2">
                {canAssign ? (
                  <Select value={e.coach_id || "none"} onValueChange={(v) => assign(e.enrollment_id, v)}>
                    <SelectTrigger data-testid={`enrollment-coach-select-${e.enrollment_id}`} className="bg-[#1C202E] border-white/10 text-white h-8 text-xs w-44"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#161923] border-white/10 text-slate-200">
                      <SelectItem value="none">{t("programs.noCoach")}</SelectItem>
                      {coaches.map((c) => <SelectItem key={c.user_id} value={c.user_id}>{c.full_name || c.email}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : <span className="text-slate-300">{e.coach?.full_name || t("programs.noCoach")}</span>}
              </td>
              <td className="px-3 py-2 text-right">
                {canActivate && e.status === "PAYMENT_APPROVED" && (
                  <Button data-testid={`enrollment-activate-${e.enrollment_id}`} size="sm" onClick={() => activate(e.enrollment_id)} className="h-8 bg-emerald-600 hover:bg-emerald-500 text-white text-xs"><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> {t("enrollments.activate")}</Button>
                )}
                {isMasterAdmin && (
                  <Button data-testid={`enrollment-delete-${e.enrollment_id}`} size="icon" variant="ghost"
                    onClick={() => removeEnrollment(e)} className="h-8 w-8 ml-1 text-red-400 hover:bg-red-500/10">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
